// src/frontend/src/services/googleAuthService.ts
import { 
  signInWithPopup, 
  signInWithRedirect, 
  getRedirectResult,
  User as FirebaseUser,
  AuthError
} from 'firebase/auth';
import { auth, googleProvider } from '../config/firebase';
import api from '../api/api';

export interface GoogleAuthResult {
  user: FirebaseUser;
  accessToken?: string;
  isNewUser: boolean;
}

export class GoogleAuthService {
  /**
   * Sign in with Google using popup
   */
  static async signInWithPopup(): Promise<GoogleAuthResult> {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      const accessToken = await user.getIdToken();
      
      // Check if this is a new user
      const isNewUser = result.providerId === 'google.com' && 
                        user.metadata.creationTime === user.metadata.lastSignInTime;
      
      return {
        user,
        accessToken,
        isNewUser
      };
    } catch (error: any) {
      console.error('Google sign-in popup error:', error);
      throw this.handleAuthError(error);
    }
  }

  /**
   * Sign in with Google using redirect (better for mobile)
   */
  static async signInWithRedirect(): Promise<void> {
    try {
      await signInWithRedirect(auth, googleProvider);
    } catch (error: any) {
      console.error('Google sign-in redirect error:', error);
      throw this.handleAuthError(error);
    }
  }

  /**
   * Get redirect result after page reload
   */
  static async getRedirectResult(): Promise<GoogleAuthResult | null> {
    try {
      const result = await getRedirectResult(auth);
      if (!result) return null;

      const user = result.user;
      const accessToken = await user.getIdToken();
      
      // Check if this is a new user
      const isNewUser = result.providerId === 'google.com' && 
                        user.metadata.creationTime === user.metadata.lastSignInTime;
      
      return {
        user,
        accessToken,
        isNewUser
      };
    } catch (error: any) {
      console.error('Google redirect result error:', error);
      throw this.handleAuthError(error);
    }
  }

  /**
   * Authenticate with backend using Firebase ID token
   */
  static async authenticateWithBackend(firebaseUser: FirebaseUser): Promise<any> {
    try {
      const idToken = await firebaseUser.getIdToken();
      
      // Send Firebase ID token to backend for verification
      const response = await api.post('/users/google-auth', {
        idToken,
        email: firebaseUser.email,
        name: firebaseUser.displayName,
        photoURL: firebaseUser.photoURL,
        uid: firebaseUser.uid
      });
      
      return response.data.data;
    } catch (error: any) {
      console.error('Backend authentication error:', error);
      throw error;
    }
  }

  /**
   * Complete Google authentication flow
   */
  static async completeGoogleAuth(usePopup: boolean = true): Promise<any> {
    let authResult: GoogleAuthResult | null;
    
    if (usePopup) {
      authResult = await this.signInWithPopup();
    } else {
      // Check for redirect result first
      authResult = await this.getRedirectResult();
      
      if (!authResult) {
        // Start redirect flow
        await this.signInWithRedirect();
        return null; // Will complete after redirect
      }
    }
    
    // Authenticate with backend
    const backendUser = await this.authenticateWithBackend(authResult.user);
    
    return {
      ...backendUser,
      isNewUser: authResult.isNewUser,
      firebaseUser: authResult.user
    };
  }

  /**
   * Handle Firebase auth errors
   */
  private static handleAuthError(error: AuthError): Error {
    switch (error.code) {
      case 'auth/popup-closed-by-user':
        return new Error('Sign-in was cancelled. Please try again.');
      case 'auth/popup-blocked':
        return new Error('Popup was blocked by browser. Please allow popups and try again.');
      case 'auth/cancelled-popup-request':
        return new Error('Sign-in was cancelled.');
      case 'auth/network-request-failed':
        return new Error('Network error. Please check your connection and try again.');
      case 'auth/too-many-requests':
        return new Error('Too many failed attempts. Please try again later.');
      default:
        return new Error(error.message || 'Authentication failed. Please try again.');
    }
  }

  /**
   * Check if device is mobile for redirect vs popup decision
   */
  static isMobileDevice(): boolean {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }
}

export default GoogleAuthService;
